import React, { useState, useEffect, useCallback } from "react";
import "./cash-flow.less";
import UserAssumption from "../UserAssumptions/UserAssumption";
import service from "../../../../services/service";
import { toast } from "react-toastify";
import {
  Invit,
  YearDataOperationalMetrics,
  YearDataOperationalMetricsIndus,
  YearDataOperationalMetricsPG,
  SectorOfOperations,
  SectorOfOperationPG,
} from "../../../../types/invit.types";
import {
  formatNumber,
  toWholeNumber,
  findLatestSemiAnnualValue,
} from "../../../../utils/financialHelper";
import { useParameters } from "../../../../context/parametersContext";

interface CashflowProps {
  invitsData: Invit[];
  selectedInvit: Invit | null;
  onInvitChange: (invit: Invit) => void;
}

export default function Cashflow({
  invitsData,
  selectedInvit,
  onInvitChange,
}: CashflowProps) {
  const heading = "Valuation at asset level using";
  const subheading = "Discounted Cash Flow Method";

  const { activeToggle } = useParameters();

  const [sectorOptions, setSectorOptions] = useState<string[]>([]);
  const [natureOptions, setNatureOptions] = useState<string[]>([]);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [dateOptions, setDateOptions] = useState<string[]>([]);

  const [selectedSector, setSelectedSector] = useState("");
  const [selectedNature, setSelectedNature] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const [isUserAssumptionOpen, setIsUserAssumptionOpen] = useState(false);
  const [userWacc, setUserWacc] = useState<number>(8.95);

  const [userAssumptionValuation, setUserAssumptionValuation] = useState<
    number | null
  >(null);

  const [userAssumptionWacc, setUserAssumptionWacc] = useState<number | null>(
    null
  );
  const [initialWaccFromDB, setInitialWaccFromDB] = useState<number | null>(
    null
  );
  const [loadingValuation, setLoadingValuation] = useState<boolean>(false);

  const [tableData, setTableData] = useState<
    { label: string; subLabel?: string; value: string }[]
  >([]);

  const [marketReportWacc, setMarketReportWacc] = useState<number | null>(null);
  const [marketReportValuation, setMarketReportValuation] = useState<
    number | null
  >(null);

  const [isProjectAvailableInAPI, setIsProjectAvailableInAPI] =
    useState<boolean>(true);

  const [yearFromWhichDataTaken, setYearFromWhichDataTaken] =
    useState<string>("");

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  useEffect(() => {
    if (!selectedInvit) return;

    const sectorsSet = new Set<string>();

    selectedInvit.operationalMetrics?.forEach((metric: any) => {
      metric.semiAnnuals?.forEach((semi: any) => {
        const sectorName = semi.sectorOfOperation?.name;
        if (sectorName) {
          const capitalizedSector = capitalize(sectorName);
          sectorsSet.add(capitalizedSector);
        }
      });
    });

    const sectors = Array.from(sectorsSet).sort();
    setSectorOptions(sectors);

    // Set the first available sector as selected
    if (sectors.length > 0) {
      setSelectedSector(sectors[0]);
    }
  }, [selectedInvit]);

  useEffect(() => {
    if (!selectedInvit) return;

    const latestOp = selectedInvit.operationalMetrics?.slice(-1)[0];
    if (!latestOp) return;

    if (latestOp.year) {
      setSelectedDate(String(latestOp.year));
    }

    const latestSemi = latestOp.semiAnnuals?.slice(-1)[0];
    if (!latestSemi) return;

    const sector = latestSemi.sectorOfOperation;

    if (sector?.name) {
      setSelectedSector(String(sector.name));
    }
    if (sector?.natureOfProject?.value != null) {
      setSelectedNature(String(sector.natureOfProject.value));
    }

    if (sector?.nameOfProject?.value != null) {
      setSelectedProject(String(sector.nameOfProject.value));
    }
  }, [selectedInvit]);

  // Populate date options from all InvITs
  useEffect(() => {
    const isPrivate = activeToggle === "Private";

    if (isPrivate && yearFromWhichDataTaken) {
      setDateOptions([yearFromWhichDataTaken]);
      setSelectedDate(yearFromWhichDataTaken);
    } else {
      setDateOptions(["HY 2024-25"]);
      setSelectedDate("HY 2024-25");
    }
  }, [invitsData, yearFromWhichDataTaken, activeToggle]);

  // Update nature options when sector changes
  useEffect(() => {
    if (!selectedInvit || !selectedSector) return;

    const natureSet = new Set<string>();

    selectedInvit.operationalMetrics?.forEach((metric) => {
      metric.semiAnnuals?.forEach((semi) => {
        const sector = semi?.sectorOfOperation;
        if (!sector) return;

        if (sector.name?.toLowerCase() === selectedSector.toLowerCase()) {
          const nature = sector.natureOfProject?.value;

          if (nature !== undefined && nature !== null) {
            const natureStr = String(nature).trim();
            if (natureStr) natureSet.add(natureStr);
          }
        }
      });
    });

    const list = Array.from(natureSet);

    setNatureOptions(list);
    setSelectedNature(list[0] || "");
    setSelectedProject("");
  }, [selectedSector, selectedInvit]);

  // Update project options when sector, nature, or date changes
  useEffect(() => {
    populateProjects();
  }, [selectedSector, selectedNature, selectedDate, selectedInvit]);

  // Update table data when project or date changes
  useEffect(() => {
    if (selectedProject !== "Project" && selectedProject !== "") {
      updateTableData();
      fetchInitialValuation();
    } else {
      setTableData([]);
      setUserAssumptionValuation(null);
      setUserAssumptionWacc(null);
      setInitialWaccFromDB(null);
      setMarketReportValuation(null);
      setMarketReportWacc(null);
      setYearFromWhichDataTaken("");
    }
  }, [selectedProject, selectedDate, activeToggle]);

  // Fetch initial valuation data from API or operationalMetrics
  const fetchInitialValuation = async () => {
    if (selectedProject === "Project" || selectedProject === "") return;

    setLoadingValuation(true);

    // Check if InvIT is private
    const isPrivate = activeToggle === "Private";

    // If private, first try to get wacc and enterpriseValue from the selected project's sectorOfOperation
    if (isPrivate && selectedInvit) {
      let projectWacc: number | null = null;
      let projectEnterpriseValue: number | null = null;
      let foundYear: string = "";

      // Find the selected project in operationalMetrics
      for (const yearData of selectedInvit.operationalMetrics as any[]) {
        for (const semiAnnual of yearData.semiAnnuals || []) {
          const sector = semiAnnual.sectorOfOperation;
          if (
            sector?.nameOfProject?.value === selectedProject &&
            sector?.name?.toLowerCase() === selectedSector.toLowerCase()
          ) {
            // Get wacc and enterpriseValue from this project
            const waccValue = sector?.wacc?.value;
            const evValue = sector?.enterpriseValue?.value;

            if (waccValue != null && waccValue !== "" && waccValue !== 0) {
              projectWacc =
                typeof waccValue === "number"
                  ? waccValue
                  : parseFloat(waccValue);
            }

            if (evValue != null && evValue !== "" && evValue !== 0) {
              projectEnterpriseValue =
                typeof evValue === "number" ? evValue : parseFloat(evValue);
            }

            // Store the year from which data was taken
            if (projectWacc !== null || projectEnterpriseValue !== null) {
              foundYear = yearData.year || "";
              console.log("found year : ", foundYear);
            }

            break;
          }
        }
        if (projectWacc !== null || projectEnterpriseValue !== null) break;
      }

      // If we found values in operationalMetrics, use them for market report card
      if (projectWacc !== null && projectEnterpriseValue !== null) {
        setMarketReportWacc(parseFloat(projectWacc.toFixed(2)));
        setMarketReportValuation(projectEnterpriseValue);
        setYearFromWhichDataTaken(foundYear);

        // Update the selected date to show the year from which data was taken
        if (foundYear) {
          setSelectedDate(foundYear);
        }
      }
    }

    // Now try to fetch from API (for user assumption card)
    try {
      const response = await service.calculateValuation(selectedProject);

      if (response.success && response.data) {
        const apiWaccPercentage = response.data.waccUsed * 100;
        const roundedWacc = parseFloat(apiWaccPercentage.toFixed(2));
        const marketWacc = response.data.standardWacc * 100;
        const roundedMarketWacc = parseFloat(marketWacc.toFixed(2));

        // For public InvITs, use API data for market report card
        if (!isPrivate) {
          setMarketReportValuation(response.data.standardEnterpriseValue);
          setMarketReportWacc(roundedMarketWacc);
        }

        setUserAssumptionValuation(response.data.actualEnterpriseValue);
        setUserAssumptionWacc(roundedWacc);
        setInitialWaccFromDB(roundedWacc);
        setIsProjectAvailableInAPI(true);
      } else {
        // Project not found in API response
        // For private InvITs, market report data might already be set from operationalMetrics
        if (!isPrivate) {
          setMarketReportValuation(null);
          setMarketReportWacc(null);
        }
        setUserAssumptionValuation(null);
        setUserAssumptionWacc(null);
        setInitialWaccFromDB(null);
        setIsProjectAvailableInAPI(false);

        // Only show error if we don't have market report data from operationalMetrics
        if (marketReportWacc === null && marketReportValuation === null) {
          toast.error("Valuation data is currently unavailable");
        }
      }
    } catch (error) {
      console.error("Error fetching initial valuation:", error);

      // For public InvITs, clear market report data on error
      if (!isPrivate) {
        setMarketReportValuation(null);
        setMarketReportWacc(null);
      }

      setUserAssumptionValuation(null);
      setUserAssumptionWacc(null);
      setInitialWaccFromDB(null);
      setIsProjectAvailableInAPI(false);

      // Only show error if we don't have market report data from operationalMetrics
      if (marketReportWacc === null && marketReportValuation === null) {
        toast.error("Valuation data is currently unavailable");
      }
    } finally {
      setLoadingValuation(false);
    }
  };

  const populateProjects = () => {
    if (!selectedInvit || !selectedSector) {
      setProjectOptions([]);
      setSelectedProject("");
      return;
    }

    const projectSet = new Set<string>();

    // Search through ONLY the selected InvIT
    selectedInvit.operationalMetrics?.forEach((metric: any) => {
      metric.semiAnnuals?.forEach((semi: any) => {
        const sector = semi.sectorOfOperation;
        const sectorName = sector?.name?.toLowerCase();
        const projectNature = sector?.natureOfProject?.value
          ?.toLowerCase()
          ?.trim();
        const projectName = sector?.nameOfProject?.value;

        // Match sector
        const matchesSector = sectorName === selectedSector.toLowerCase();

        // Match nature
        let matchesNature = false;
        if (!selectedNature) {
          matchesNature = true;
        } else if (selectedNature.toLowerCase() === "toll") {
          matchesNature = projectNature === "toll" || projectNature === "bot";
        } else if (selectedNature.toLowerCase() === "ham") {
          matchesNature =
            projectNature === "ham" ||
            (projectNature && projectNature.includes("hybrid"));
        } else if (selectedNature.toLowerCase() === "annuity") {
          matchesNature =
            projectNature === "annuity" &&
            !projectNature.includes("toll") &&
            !projectNature.includes("ham");
        } else if (selectedNature.toLowerCase() === "toll+annuity") {
          matchesNature =
            projectNature &&
            projectNature.includes("toll") &&
            projectNature.includes("annuity");
        } else if (selectedNature.toLowerCase() === "bess") {
          matchesNature =
            projectNature === "bess" ||
            (projectNature &&
              (projectNature.includes("battery") ||
                projectNature.includes("bess")));
        } else if (selectedNature.toLowerCase() === "transmission") {
          matchesNature =
            projectNature && projectNature.includes("transmission");
        } else if (selectedNature.toLowerCase() === "solar") {
          matchesNature = projectNature && projectNature.includes("solar");
        } else if (selectedNature.toLowerCase() === "generation") {
          matchesNature = projectNature && projectNature.includes("generation");
        } else {
          matchesNature = projectNature === selectedNature.toLowerCase();
        }

        if (matchesSector && matchesNature && projectName) {
          projectSet.add(projectName);
        }
      });
    });

    const projects = Array.from(projectSet).sort();
    setProjectOptions(projects);

    if (projects.length === 1) {
      setSelectedProject(projects[0]);
    } else if (projects.length > 1) {
      setSelectedProject(projects[1]);
    } else {
      setSelectedProject("");
    }
  };

  const updateTableData = () => {
    if (!selectedInvit) {
      setTableData([]);
      return;
    }

    if (
      selectedProject === "Project" ||
      selectedProject === "" ||
      !selectedDate
    ) {
      setTableData([]);
      return;
    }

    // Find the project data from the selected InvIT only
    let projectData: any = null;

    for (const yearData of selectedInvit.operationalMetrics as any[]) {
      for (const semiAnnual of yearData.semiAnnuals || []) {
        const sector = semiAnnual.sectorOfOperation;
        if (
          sector?.nameOfProject?.value === selectedProject &&
          sector?.name?.toLowerCase() === selectedSector.toLowerCase()
        ) {
          projectData = sector;
          break;
        }
      }
      if (projectData) break;
    }

    if (!projectData) {
      setTableData([]);
      return;
    }

    // Build table data based on sector type
    const data: Array<{ label: string; subLabel?: string; value: string }> = [];

    data.push({
      label: "Sector of operation",
      value: projectData.name || "---",
    });

    data.push({
      label: "Name of the project",
      value: projectData.nameOfProject?.value?.toString() || "---",
    });

    data.push({
      label: "Nature of the project",
      value: projectData.natureOfProject?.value?.toString() || "---",
    });

    data.push({
      label: "Status of the project",
      value: projectData.statusOfProject?.value?.toString() || "---",
    });

    if (projectData.name?.toLowerCase() === "power") {
      // Power specific fields
      data.push({
        label: "Transmission Line Length",
        subLabel: "(in km)",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "transmissionLineLength"
          ) || "---",
      });

      data.push({
        label: "Transformation capacity",
        subLabel: "(in MVA)",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "transformationCapacity"
          ) || "---",
      });

      data.push({
        label: "Geographical Presence",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "geographicalPresence"
          ) || "---",
      });

      data.push({
        label: "Contract life remaining",
        subLabel: "(in years)",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "contractLifeRemaining"
          ) || "---",
      });
    } else if (projectData.name?.toLowerCase() === "roads") {
      // Roads specific fields
      data.push({
        label: "Total Length of Roads",
        subLabel: "(in kms)",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "totalLengthOfRoads"
          ) || "---",
      });

      data.push({
        label: "Total Lane Kms",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "totalLaneKms"
          ) || "---",
      });

      data.push({
        label: "Geographical Presence",
        subLabel: "(Name of State)",
        value: projectData.geographicalPresence?.value?.toString() || "---",
      });

      data.push({
        label: "Concession life remaining",
        subLabel: "(in years)",
        value:
          findLatestSemiAnnualValue(
            selectedInvit.operationalMetrics,
            "concessionLifeRemaining"
          ) || "---",
      });
    }

    setTableData(data);
  };

  const handleSectorChange = (sector: string) => {
    setSelectedSector(sector);
  };

  const handleNatureChange = (nature: string) => {
    setSelectedNature(nature);
  };

  const handleEditClick = (cardIndex: number) => {
    if (cardIndex === 0 && isProjectAvailableInAPI) {
      // "Based on Users Assumption" card - only allow edit if project is available
      setIsUserAssumptionOpen(true);
    }
  };

  const handleValuationUpdate = useCallback(
    (valuation: number, wacc: number) => {
      setUserAssumptionValuation(valuation);
      setUserAssumptionWacc(wacc);
    },
    []
  );

  const cards = [
    {
      title: "Based on Users Assumption",
      rate:
        userAssumptionWacc !== null
          ? `${userAssumptionWacc.toFixed(2)}%`
          : "---",
      rateLabel: "Effective Discount Rate",
      valuation:
        userAssumptionValuation !== null
          ? toWholeNumber(userAssumptionValuation)
          : "---",

      valuationLabel: "Valuation (Rs. Crore)",
      hasPopup: true,
    },
    {
      title: "Based on Market Report",
      rate:
        marketReportWacc !== null ? `${marketReportWacc.toFixed(2)}%` : "---",
      rateLabel: "Effective Discount Rate",
      valuation:
        marketReportValuation !== null
          ? toWholeNumber(marketReportValuation)
          : "---",

      valuationLabel: "Valuation (Rs. Crore)",
      hasPopup: false,
    },
  ];

  const editIcon = new URL("../../../../assets/editIcon.png", import.meta.url)
    .href;

  return (
    <section className="cash-flow--segment">
      <div className="cash-flow">
        <div className="cash-flow__head">
          <div className="cash-flow__title">
            <h3>
              {heading} <br />
              <span>{subheading}</span>
            </h3>
          </div>

          <div className="cash-flow__filters">
            <div className="cash-flow__filter-group">
              <select
                className="cash-flow__dropdown"
                value={selectedSector}
                onChange={(e) => handleSectorChange(e.target.value)}
                disabled={sectorOptions.length <= 1}
              >
                {sectorOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              <select
                className="cash-flow__dropdown"
                value={selectedNature}
                onChange={(e) => handleNatureChange(e.target.value)}
                disabled={!selectedSector || selectedSector === "Sector"}
              >
                {natureOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              <select
                className="cash-flow__dropdown"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                disabled={projectOptions.length === 0}
              >
                {projectOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              <select
                className="cash-flow__dropdown"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={dateOptions.length === 0}
              >
                {dateOptions.length === 0 ? (
                  <option value="">No dates available</option>
                ) : (
                  dateOptions.map((opt, i) => (
                    <option key={i} value={opt}>
                      {opt}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="cash-flow__content">
          <div className="cash-flow__cards">
            {cards.map((card, idx) => (
              <div key={idx} className="cash-flow__card">
                <div className="cash-flow__card-header">
                  <h4 className="cash-flow__card-title">{card.title}</h4>
                  {card.hasPopup && (
                    <img
                      src={editIcon}
                      alt="Edit"
                      className="cash-flow__card-edit"
                      width={20}
                      height={20}
                      onClick={() => handleEditClick(idx)}
                      style={{
                        cursor: isProjectAvailableInAPI
                          ? "pointer"
                          : "not-allowed",
                        opacity: isProjectAvailableInAPI ? 1 : 0.5,
                      }}
                    />
                  )}
                </div>

                <div className="cash-flow__card-body">
                  <div className="cash-flow__card-metric">
                    <div className="cash-flow__card-value">
                      {loadingValuation && idx === 0 ? "Loading..." : card.rate}
                    </div>
                    <div className="cash-flow__card-label">
                      {card.rateLabel}
                    </div>
                  </div>
                  <div className="cash-flow__card-metric">
                    <div className="cash-flow__card-value">
                      {loadingValuation && idx === 0
                        ? "Loading..."
                        : card.valuation}
                    </div>
                    <div className="cash-flow__card-label">
                      {card.valuationLabel}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="cash-flow__table-wrapper">
            {tableData.length > 0 ? (
              <table className="cash-flow__table">
                <thead>
                  <tr>
                    <th>Name of Asset</th>
                    <th>
                      {selectedProject !== "Project" && selectedProject !== ""
                        ? selectedProject
                        : "Select Project"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        {row.label}
                        {row.subLabel && (
                          <>
                            <br />
                            <span className="cash-flow__table-sublabel">
                              {row.subLabel}
                            </span>
                          </>
                        )}
                      </td>
                      <td>{formatNumber(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="cash-flow__table-placeholder">
                <p>
                  {!selectedSector || selectedSector === "Sector"
                    ? "Please select a sector to view projects"
                    : !selectedProject || selectedProject === "Project"
                    ? "Please select a project to view details"
                    : "No data available for selected project"}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="cash-flow__disclaimer">
          <h4>
            Disclaimer : Methodology - 10 - Year Discounted Cash Flow Method
          </h4>
          <p>
            The income approach is based on the premise that value of an
            income-producing asset is a function of future benefits and income
            derived from that asset.
          </p>
          <p>
            Net operating income (NOI) has primarily been used to arrive at the
            value of the commercial office assets. The projected future cash
            flows from the property are based on existing lease terms for the
            operational area till the expiry of the leases or re-negotiation,
            whichever is earlier. These cash flows have been projected for
            10-year duration from the date of valuation and 10th year NOI is
            used for assessment of terminal value. NOI of 10th year is
            capitalised at an appropriate rate to arrive at the terminal value.
            These future cash flows and terminal value are then discounted to
            present-day value (valuation date) at an appropriate discount rate.
          </p>
          <p>
            Note: This valuation model is exclusively designed for completed
            commercial office and retail spaces. It is not applicable to
            properties under construction. It may also be noted that there may
            be a 10-15% variance between published valuations and the results
            generated by the model and the variation could be higher for the
            properties where the occupancy rates are below 80%.
          </p>
        </div>
        <UserAssumption
          isOpen={isUserAssumptionOpen}
          onClose={() => setIsUserAssumptionOpen(false)}
          projectName={selectedProject}
          onValuationUpdate={handleValuationUpdate}
          currentWacc={userAssumptionWacc}
          initialWaccFromDB={initialWaccFromDB}
        />
      </div>
    </section>
  );
}
